import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Card,
  CardContent,
  Chip,
  Stepper,
  Step,
  StepLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@mui/material';
import {
  Payment,
  Send,
  AccountBalance,
  CreditCard,
  Receipt,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { studentAPI, paymentAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const SubmitStudents = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Payment dialog state
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // Payment form state
  const [paymentMode, setPaymentMode] = useState('online');
  const [onlinePaymentType, setOnlinePaymentType] = useState('');
  const [paymentDetails, setPaymentDetails] = useState({
    // Online details
    transactionId: '',
    upiId: '',
    bankName: '',
    accountNumber: '',
    // Offline details
    offlineBankName: '',
    branchName: '',
    offlineAccountNumber: '',
    ifscCode: '',
    chequeNumber: '',
    ddNumber: '',
    receiptNumber: '',
    // Common
    amount: '',
    remittedDate: new Date().toISOString().split('T')[0],
    remarks: '',
  });

  useEffect(() => {
    fetchStudentsWithoutPayment();
  }, []);

  const fetchStudentsWithoutPayment = useCallback(async () => {
    try {
      setLoading(true);
      // Get all pending students for this branch
      const response = await studentAPI.getAll({
        status: 'pending',
        limit: 1000,
      });

      const allStudents = response.data.data || [];

      // Get all payments and find students already linked to payments
      const paymentsResponse = await paymentAPI.getAll({ limit: 1000 });
      const payments = paymentsResponse.data.data || [];

      // Get all student IDs that are already in a payment
      const studentsWithPayment = new Set();
      payments.forEach(payment => {
        if (payment.students) {
          payment.students.forEach(s => {
            const studentId = typeof s === 'string' ? s : s._id;
            studentsWithPayment.add(studentId);
          });
        }
      });

      // Filter out students that already have payment
      const studentsWithoutPayment = allStudents.filter(
        student => !studentsWithPayment.has(student._id)
      );

      setStudents(studentsWithoutPayment);
    } catch (error) {
      console.error('Error fetching students:', error);
      // Only show error toast for actual API errors
      if (error.response?.status >= 400) {
        toast.error(error.response?.data?.message || 'Failed to load students');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedStudents(students.map(s => s._id));
    } else {
      setSelectedStudents([]);
    }
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      }
      return [...prev, studentId];
    });
  };

  const handleOpenPaymentDialog = () => {
    if (selectedStudents.length === 0) {
      toast.warning('Please select at least one student');
      return;
    }
    setPaymentDialog(true);
    setActiveStep(0);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialog(false);
    setActiveStep(0);
    setPaymentMode('online');
    setOnlinePaymentType('');
    setPaymentDetails({
      transactionId: '',
      upiId: '',
      bankName: '',
      accountNumber: '',
      offlineBankName: '',
      branchName: '',
      offlineAccountNumber: '',
      ifscCode: '',
      chequeNumber: '',
      ddNumber: '',
      receiptNumber: '',
      amount: '',
      remittedDate: new Date().toISOString().split('T')[0],
      remarks: '',
    });
  };

  const handlePaymentDetailsChange = (e) => {
    const { name, value } = e.target;
    setPaymentDetails(prev => ({ ...prev, [name]: value }));
  };

  const validateStep1 = () => {
    if (paymentMode === 'online' && !onlinePaymentType) {
      toast.error('Please select payment type (Net Banking or UPI)');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!paymentDetails.amount || parseFloat(paymentDetails.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return false;
    }
    if (!paymentDetails.remittedDate) {
      toast.error('Please enter the remitted date');
      return false;
    }

    if (paymentMode === 'online') {
      if (!paymentDetails.transactionId) {
        toast.error('Please enter the transaction ID');
        return false;
      }
      if (onlinePaymentType === 'upi' && !paymentDetails.upiId) {
        toast.error('Please enter the UPI ID');
        return false;
      }
      if (onlinePaymentType === 'netbanking' && !paymentDetails.bankName) {
        toast.error('Please enter the bank name');
        return false;
      }
    } else {
      if (!paymentDetails.offlineBankName) {
        toast.error('Please enter the bank name');
        return false;
      }
      if (!paymentDetails.branchName) {
        toast.error('Please enter the bank branch name');
        return false;
      }
    }

    return true;
  };

  const handleNext = () => {
    if (activeStep === 0 && !validateStep1()) return;
    if (activeStep === 1 && !validateStep2()) return;
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmitWithPayment = async () => {
    try {
      setSubmitting(true);

      // Prepare payment data
      const paymentData = {
        students: selectedStudents,
        paymentMode,
        amount: parseFloat(paymentDetails.amount),
        studentCount: selectedStudents.length,
        remittedDate: paymentDetails.remittedDate,
        remarks: paymentDetails.remarks,
      };

      // Add online or offline details based on mode
      if (paymentMode === 'online') {
        paymentData.onlineDetails = {
          paymentType: onlinePaymentType,
          transactionId: paymentDetails.transactionId,
          upiId: paymentDetails.upiId || '',
          bankName: paymentDetails.bankName || '',
          accountNumber: paymentDetails.accountNumber || '',
        };
      } else {
        paymentData.offlineDetails = {
          bankName: paymentDetails.offlineBankName,
          branchName: paymentDetails.branchName,
          accountNumber: paymentDetails.offlineAccountNumber || '',
          ifscCode: paymentDetails.ifscCode || '',
          chequeNumber: paymentDetails.chequeNumber || '',
          ddNumber: paymentDetails.ddNumber || '',
          receiptNumber: paymentDetails.receiptNumber || '',
        };
      }

      // Create payment
      const response = await paymentAPI.create(paymentData);

      toast.success(`Payment recorded successfully! Reference: ${response.data.data.paymentReference}`);
      handleClosePaymentDialog();
      setSelectedStudents([]);
      fetchStudentsWithoutPayment();
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error(error.response?.data?.message || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const steps = ['Select Payment Mode', 'Enter Payment Details', 'Review & Submit'];

  const getSelectedStudentDetails = () => {
    return students.filter(s => selectedStudents.includes(s._id));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Submit Students for Approval
        </Typography>
        <Button
          variant="contained"
          startIcon={<Payment />}
          onClick={handleOpenPaymentDialog}
          disabled={selectedStudents.length === 0}
        >
          Add Payment & Submit ({selectedStudents.length})
        </Button>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Select students below and add payment details to submit them for SuperAdmin approval.
        Students without payment information cannot be approved.
      </Alert>

      {/* Students Table */}
      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedStudents.length > 0 && selectedStudents.length < students.length}
                    checked={students.length > 0 && selectedStudents.length === students.length}
                    onChange={handleSelectAll}
                    disabled={loading || students.length === 0}
                  />
                </TableCell>
                <TableCell><strong>Form No</strong></TableCell>
                <TableCell><strong>Student Name</strong></TableCell>
                <TableCell><strong>Course</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Phone</strong></TableCell>
                <TableCell><strong>Submitted Date</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : students.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No students pending payment submission
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                students.map((student) => (
                  <TableRow
                    key={student._id}
                    hover
                    selected={selectedStudents.includes(student._id)}
                    onClick={() => handleSelectStudent(student._id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedStudents.includes(student._id)}
                        onChange={() => handleSelectStudent(student._id)}
                      />
                    </TableCell>
                    <TableCell>{student.formNumber || '-'}</TableCell>
                    <TableCell>{student.personalInfo?.nameAsPerTC || '-'}</TableCell>
                    <TableCell>
                      {student.course?.name || '-'}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {student.course?.code || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{student.personalInfo?.email || '-'}</TableCell>
                    <TableCell>{student.personalInfo?.phone || '-'}</TableCell>
                    <TableCell>{formatDate(student.submittedAt)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialog}
        onClose={handleClosePaymentDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Payment color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Add Payment Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 0: Select Payment Mode */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                How was the payment made?
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={paymentMode}
                  onChange={(e) => {
                    setPaymentMode(e.target.value);
                    setOnlinePaymentType('');
                  }}
                >
                  <FormControlLabel
                    value="online"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CreditCard color="primary" />
                        <Typography>Online Payment (Net Banking / UPI)</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="offline"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountBalance color="primary" />
                        <Typography>Offline Payment (Bank Deposit / Cheque / DD)</Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              {paymentMode === 'online' && (
                <Box sx={{ mt: 3, pl: 4 }}>
                  <FormControl component="fieldset">
                    <FormLabel>Select Online Payment Type</FormLabel>
                    <RadioGroup
                      value={onlinePaymentType}
                      onChange={(e) => setOnlinePaymentType(e.target.value)}
                      sx={{ mt: 1 }}
                    >
                      <FormControlLabel
                        value="netbanking"
                        control={<Radio />}
                        label="Net Banking"
                      />
                      <FormControlLabel
                        value="upi"
                        control={<Radio />}
                        label="UPI"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}
            </Box>
          )}

          {/* Step 1: Enter Payment Details */}
          {activeStep === 1 && (
            <Box>
              <Grid container spacing={3}>
                {/* Common Fields */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Amount (INR)"
                    name="amount"
                    type="number"
                    value={paymentDetails.amount}
                    onChange={handlePaymentDetailsChange}
                    InputProps={{ inputProps: { min: 1 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Remitted Date"
                    name="remittedDate"
                    type="date"
                    value={paymentDetails.remittedDate}
                    onChange={handlePaymentDetailsChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Online Payment Fields */}
                {paymentMode === 'online' && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}>
                        <Chip label="Online Payment Details" size="small" />
                      </Divider>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        label="Transaction ID / Reference Number"
                        name="transactionId"
                        value={paymentDetails.transactionId}
                        onChange={handlePaymentDetailsChange}
                      />
                    </Grid>
                    {onlinePaymentType === 'upi' && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="UPI ID"
                          name="upiId"
                          value={paymentDetails.upiId}
                          onChange={handlePaymentDetailsChange}
                          placeholder="example@upi"
                        />
                      </Grid>
                    )}
                    {onlinePaymentType === 'netbanking' && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            required
                            label="Bank Name"
                            name="bankName"
                            value={paymentDetails.bankName}
                            onChange={handlePaymentDetailsChange}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Account Number (Last 4 digits)"
                            name="accountNumber"
                            value={paymentDetails.accountNumber}
                            onChange={handlePaymentDetailsChange}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}

                {/* Offline Payment Fields */}
                {paymentMode === 'offline' && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}>
                        <Chip label="Bank Details" size="small" />
                      </Divider>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        label="Bank Name"
                        name="offlineBankName"
                        value={paymentDetails.offlineBankName}
                        onChange={handlePaymentDetailsChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        label="Bank Branch Name"
                        name="branchName"
                        value={paymentDetails.branchName}
                        onChange={handlePaymentDetailsChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Account Number"
                        name="offlineAccountNumber"
                        value={paymentDetails.offlineAccountNumber}
                        onChange={handlePaymentDetailsChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="IFSC Code"
                        name="ifscCode"
                        value={paymentDetails.ifscCode}
                        onChange={handlePaymentDetailsChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}>
                        <Chip label="Payment Instrument Details" size="small" />
                      </Divider>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Cheque Number"
                        name="chequeNumber"
                        value={paymentDetails.chequeNumber}
                        onChange={handlePaymentDetailsChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="DD Number"
                        name="ddNumber"
                        value={paymentDetails.ddNumber}
                        onChange={handlePaymentDetailsChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Receipt Number"
                        name="receiptNumber"
                        value={paymentDetails.receiptNumber}
                        onChange={handlePaymentDetailsChange}
                      />
                    </Grid>
                  </>
                )}

                {/* Remarks */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Additional Remarks (Optional)"
                    name="remarks"
                    value={paymentDetails.remarks}
                    onChange={handlePaymentDetailsChange}
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 2: Review & Submit */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Review Payment Summary
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Payment Information
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Mode:</Typography>
                          <Chip
                            label={paymentMode === 'online' ? 'Online' : 'Offline'}
                            size="small"
                            color={paymentMode === 'online' ? 'primary' : 'secondary'}
                          />
                        </Box>
                        {paymentMode === 'online' && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Type:</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {onlinePaymentType === 'upi' ? 'UPI' : 'Net Banking'}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Amount:</Typography>
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            ₹{parseFloat(paymentDetails.amount || 0).toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Remitted Date:</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {formatDate(paymentDetails.remittedDate)}
                          </Typography>
                        </Box>
                        {paymentMode === 'online' && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Transaction ID:</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {paymentDetails.transactionId}
                            </Typography>
                          </Box>
                        )}
                        {paymentMode === 'offline' && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Bank:</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {paymentDetails.offlineBankName}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Students ({selectedStudents.length})
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                        {getSelectedStudentDetails().map((student, index) => (
                          <Box
                            key={student._id}
                            sx={{
                              py: 1,
                              borderBottom: index < selectedStudents.length - 1 ? '1px solid #eee' : 'none',
                            }}
                          >
                            <Typography variant="body2" fontWeight={600}>
                              {student.personalInfo?.nameAsPerTC}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {student.formNumber} | {student.course?.name}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 3 }}>
                Once submitted, the payment and student details will be sent to SuperAdmin for approval.
                The payment reference number will be generated automatically.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClosePaymentDialog} disabled={submitting}>
            Cancel
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          {activeStep > 0 && (
            <Button onClick={handleBack} disabled={submitting}>
              Back
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmitWithPayment}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
            >
              {submitting ? 'Submitting...' : 'Submit with Payment'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubmitStudents;
